// Shared CSP helpers for the Bazak apps' security headers — the website/platform
// next.config headers() and the assistant middleware. Single-sources what must stay
// identical across the three: env-URL normalization, directive assembly, and the
// PostHog report wiring.

/** Bare origin only — a stray path/token in the env URL would otherwise break CSP parsing. */
export function toOrigin(url: string | undefined): string {
  try {
    return url ? new URL(url).origin : '';
  } catch {
    return '';
  }
}

/** Drops empty tokens — e.g. an unset CDN origin (dev) that must not land in the policy. */
export function cspDirective(...parts: string[]): string {
  return parts.filter(Boolean).join(' ');
}

export interface BuiltCsp {
  policy: string;
  reportingEndpoints?: string;
}

/**
 * Assemble an enforced CSP from its directives plus PostHog violation reporting. PostHog's
 * report endpoint needs the trailing slash on `/report/`; `report-to` names the endpoint the
 * `Reporting-Endpoints` header declares. Without a token (dev) the policy is returned as-is
 * and no reporting header is emitted.
 */
export function buildCsp({
  directives,
  posthogHost,
  posthogToken,
}: {
  directives: string[];
  posthogHost: string;
  posthogToken: string | undefined;
}): BuiltCsp {
  if (!posthogToken) {
    return { policy: directives.join('; ') };
  }
  const reportUri = `${posthogHost}/report/?token=${encodeURIComponent(posthogToken)}`;
  return {
    policy: [...directives, `report-uri ${reportUri}`, 'report-to posthog'].join('; '),
    reportingEndpoints: `posthog="${reportUri}"`,
  };
}
